import type { User } from '@/domains/users';
import { PageHeader } from '@/shared/components/PageHeader';
import { ALLOWED_TEXT_PATTERN, TEXT_VALIDATION_MSG } from '@/shared/lib/validation';
import { showErrorToast } from '@/shared/notifications/toast-utils';
import { Avatar, AvatarFallback } from '@/shared/ui/avatar';
import { Badge } from '@/shared/ui/badge';
import { Button } from '@/shared/ui/button';
import { Input } from '@/shared/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/shared/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/shared/ui/table';
import { Textarea } from '@/shared/ui/textarea';
import {
  AlertCircle,
  ArrowLeft,
  ArrowRight,
  Eye,
  Loader2,
  Plus,
  Search,
  UserPlus,
  Users as UsersIcon,
  X,
} from 'lucide-react';
import { useState } from 'react';
import type { TeamListItem } from '../types/team';

interface TeamsViewProps {
  teams?: TeamListItem[];
  isLoading?: boolean;
  isError?: boolean;
  onTeamClick?: (teamId: string) => void;
  allUsers?: User[];
  onCreateTeam?: (
    name: string,
    description: string,
    maxCapacity: number | null,
    memberIds: number[],
    teamLeadId: number | null,
  ) => Promise<void>;
}

export function TeamsView({
  teams = [],
  isLoading = false,
  isError = false,
  onTeamClick,
  allUsers = [],
  onCreateTeam,
}: TeamsViewProps) {
  const [searchTerm, setSearchTerm] = useState('');
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [createStage, setCreateStage] = useState<1 | 2>(1);
  const [newTeamName, setNewTeamName] = useState('');
  const [newTeamDescription, setNewTeamDescription] = useState('');
  const [newTeamCapacity, setNewTeamCapacity] = useState<string>('');
  const [selectedMembers, setSelectedMembers] = useState<string[]>([]);
  const [selectedLeadId, setSelectedLeadId] = useState<string>('');
  const [memberSearchTerm, setMemberSearchTerm] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [validationErrors, setValidationErrors] = useState<Record<string, string>>({});

  const filteredTeams = teams.filter(
    (team) =>
      team.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      (team.description ?? '').toLowerCase().includes(searchTerm.toLowerCase()),
  );

  const getInitials = (name: string) => {
    return name
      .split(' ')
      .map((n) => n[0])
      .join('')
      .toUpperCase();
  };

  const getUtilizationColor = (rate: number) => {
    if (rate >= 80) return 'text-red-600 dark:text-red-400';
    if (rate >= 60) return 'text-yellow-600 dark:text-yellow-400';
    return 'text-green-600 dark:text-green-400';
  };

  const handleCreateTeam = async () => {
    if (!newTeamName.trim()) return;
    setIsSubmitting(true);
    try {
      const maxCapacity = newTeamCapacity ? Number.parseInt(newTeamCapacity) : null;
      await onCreateTeam?.(
        newTeamName.trim(),
        newTeamDescription.trim(),
        maxCapacity,
        selectedMembers.map(Number),
        selectedLeadId ? Number(selectedLeadId) : null,
      );
      handleCloseCreateModal();
    } catch (error: any) {
      showErrorToast('Failed to Create Team', error?.message || 'An unexpected error occurred.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleCloseCreateModal = () => {
    setShowCreateModal(false);
    setCreateStage(1);
    setNewTeamName('');
    setNewTeamDescription('');
    setNewTeamCapacity('');
    setSelectedMembers([]);
    setSelectedLeadId('');
    setMemberSearchTerm('');
    setValidationErrors({});
  };

  const validateStage1 = (): boolean => {
    const errors: Record<string, string> = {};
    const name = newTeamName.trim();
    const description = newTeamDescription.trim();

    if (!name) {
      errors.name = 'Team name is required';
    } else if (name.length > 50) {
      errors.name = 'Team name must be 50 characters or less';
    } else if (!ALLOWED_TEXT_PATTERN.test(name)) {
      errors.name = TEXT_VALIDATION_MSG;
    }

    if (description && description.length > 50) {
      errors.description = 'Description must be 50 characters or less';
    } else if (description && !ALLOWED_TEXT_PATTERN.test(description)) {
      errors.description = TEXT_VALIDATION_MSG;
    }

    if (newTeamCapacity) {
      const cap = Number.parseInt(newTeamCapacity);
      if (Number.isNaN(cap) || cap < 1 || cap > 50) {
        errors.capacity = 'Daily job capacity must be between 1 and 50';
      }
    }

    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleNextStage = () => {
    if (createStage === 1 && validateStage1()) {
      setCreateStage(2);
    }
  };

  const handlePreviousStage = () => {
    setCreateStage(1);
  };

  const toggleMember = (userId: string) => {
    if (selectedMembers.includes(userId)) {
      setSelectedMembers(selectedMembers.filter((id) => id !== userId));
      // A team lead must remain a member — clear the lead if they're removed.
      if (selectedLeadId === userId) setSelectedLeadId('');
    } else {
      setSelectedMembers([...selectedMembers, userId]);
    }
  };

  const filteredAvailableUsers = allUsers.filter(
    (user) =>
      user.name.toLowerCase().includes(memberSearchTerm.toLowerCase()) ||
      user.email.toLowerCase().includes(memberSearchTerm.toLowerCase()) ||
      user.team.toLowerCase().includes(memberSearchTerm.toLowerCase()),
  );

  const totalMembers = teams.reduce((sum, t) => sum + t.memberCount, 0);
  const totalActiveJobs = teams.reduce((sum, t) => sum + t.activeJobs, 0);

  if (isLoading) {
    return (
      <div className="h-full flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-orange-600" />
      </div>
    );
  }

  if (isError) {
    return (
      <div className="h-full flex items-center justify-center">
        <div className="text-center">
          <AlertCircle className="w-12 h-12 mx-auto mb-3 text-red-400" />
          <p className="text-muted-foreground">Failed to load teams</p>
        </div>
      </div>
    );
  }

  return (
    <div className="h-full flex flex-col overflow-hidden">
      {/* Header */}
      <PageHeader
        title="Teams"
        subtitle={`${teams.length} teams • ${totalMembers} members • ${totalActiveJobs} active jobs`}
        icon={<UsersIcon className="w-7 h-7 text-orange-600 dark:text-orange-400" />}
        breadcrumbs={[{ label: 'Teams', current: true }]}
        actions={
          <Button
            className="bg-orange-600 hover:bg-orange-700 text-white"
            onClick={() => setShowCreateModal(true)}
          >
            <Plus className="w-4 h-4 mr-2" />
            Create Team
          </Button>
        }
      />

      {/* Scrollable Content */}
      <div className="flex-1 overflow-y-auto">
        {/* Search Bar */}
        <div className="bg-card/50 border border-border rounded-lg p-4 mb-4">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
            <Input
              placeholder="Search teams..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-9 bg-background border-border text-foreground"
            />
          </div>
        </div>

        {/* Teams Table */}
        <div className="bg-card border border-border rounded-lg overflow-hidden">
          <Table>
            <TableHeader className="sticky top-0 bg-card/90 backdrop-blur-sm z-10">
              <TableRow className="border-border hover:bg-transparent">
                <TableHead className="text-muted-foreground font-medium">Team</TableHead>
                <TableHead className="text-muted-foreground font-medium">Description</TableHead>
                <TableHead className="text-muted-foreground font-medium text-center">
                  Members
                </TableHead>
                <TableHead className="text-muted-foreground font-medium text-center">
                  Daily Capacity
                </TableHead>
                <TableHead className="text-muted-foreground font-medium text-center">
                  Active Jobs
                </TableHead>
                <TableHead className="text-muted-foreground font-medium text-center">
                  Completed
                </TableHead>
                <TableHead className="text-muted-foreground font-medium">Utilisation</TableHead>
                <TableHead className="text-muted-foreground font-medium">Team Members</TableHead>
                <TableHead className="text-muted-foreground font-medium text-right">
                  Actions
                </TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filteredTeams.map((team) => (
                <TableRow key={team.id} className="border-border hover:bg-accent/50">
                  <TableCell>
                    <div className="font-semibold text-foreground">{team.name}</div>
                  </TableCell>
                  <TableCell>
                    <div className="text-sm text-muted-foreground max-w-xs truncate">
                      {team.description ?? '—'}
                    </div>
                  </TableCell>
                  <TableCell className="text-center">
                    <Badge
                      variant="outline"
                      className="bg-blue-50 dark:bg-blue-950/30 text-blue-600 dark:text-blue-400 border-blue-200 dark:border-blue-800"
                    >
                      {team.memberCount}
                    </Badge>
                  </TableCell>
                  <TableCell className="text-center">
                    <span className="text-sm text-muted-foreground">{team.maxCapacity ?? '—'}</span>
                  </TableCell>
                  <TableCell className="text-center">
                    <span className="text-sm font-semibold text-blue-600 dark:text-blue-400">
                      {team.activeJobs}
                    </span>
                  </TableCell>
                  <TableCell className="text-center">
                    <span className="text-sm font-semibold text-green-600 dark:text-green-400">
                      {team.completedJobs}
                    </span>
                  </TableCell>
                  <TableCell>
                    <div className="flex items-center gap-2">
                      <div className="w-20 h-2 bg-muted rounded-full overflow-hidden">
                        <div
                          className={`h-full transition-all ${
                            team.utilizationRate >= 80
                              ? 'bg-red-500'
                              : team.utilizationRate >= 60
                                ? 'bg-yellow-500'
                                : 'bg-green-500'
                          }`}
                          style={{ width: `${team.utilizationRate}%` }}
                        />
                      </div>
                      <span
                        className={`text-sm font-semibold ${getUtilizationColor(team.utilizationRate)}`}
                      >
                        {team.utilizationRate}%
                      </span>
                    </div>
                  </TableCell>
                  <TableCell>
                    <div className="flex items-center gap-2">
                      <div className="flex -space-x-2">
                        {team.members.slice(0, 3).map((member) => (
                          <Avatar
                            key={member.id}
                            className="w-7 h-7 border-2 border-card bg-blue-600 text-white"
                          >
                            <AvatarFallback className="bg-blue-600 text-white text-xs">
                              {getInitials(member.name)}
                            </AvatarFallback>
                          </Avatar>
                        ))}
                      </div>
                      {team.memberCount > 3 && (
                        <span className="text-xs text-muted-foreground">
                          +{team.memberCount - 3}
                        </span>
                      )}
                    </div>
                  </TableCell>
                  <TableCell className="text-right">
                    <Button
                      variant="ghost"
                      size="sm"
                      className="h-8 w-8 p-0 text-blue-600 dark:text-blue-400 hover:text-blue-700 dark:hover:text-blue-300"
                      onClick={() => onTeamClick?.(String(team.id))}
                    >
                      <Eye className="w-4 h-4" />
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>

        {filteredTeams.length === 0 && !isLoading && (
          <div className="flex items-center justify-center h-[300px] text-muted-foreground">
            <div className="text-center">
              <Search className="w-12 h-12 mx-auto mb-3 opacity-50" />
              <p>No teams found</p>
            </div>
          </div>
        )}

        {/* Results Footer */}
        <div className="mt-4 text-sm text-muted-foreground">
          Showing {filteredTeams.length} of {teams.length} teams
        </div>
      </div>

      {/* Create Team Modal - Two Stage */}
      {showCreateModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-card rounded-lg border border-border shadow-xl max-w-2xl w-full mx-4 max-h-[80vh] flex flex-col">
            <div className="flex items-center justify-between p-4 border-b border-border">
              <div>
                <h2 className="text-lg font-semibold text-foreground">Create New Team</h2>
                <p className="text-sm text-muted-foreground">Stage {createStage} of 2</p>
              </div>
              <button
                onClick={handleCloseCreateModal}
                className="text-muted-foreground hover:text-foreground"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            {/* Stage 1: Team Details */}
            {createStage === 1 && (
              <>
                <div className="p-6 space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-foreground mb-1">
                      Team Name *
                    </label>
                    <Input
                      placeholder="Enter team name..."
                      value={newTeamName}
                      onChange={(e) => {
                        setNewTeamName(e.target.value);
                        if (validationErrors.name)
                          setValidationErrors((prev) => ({ ...prev, name: '' }));
                      }}
                      maxLength={50}
                      autoFocus
                    />
                    {validationErrors.name && (
                      <p className="text-xs text-red-600 dark:text-red-400 mt-1">
                        {validationErrors.name}
                      </p>
                    )}
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-foreground mb-1">
                      Description
                    </label>
                    <Textarea
                      placeholder="Enter team description..."
                      value={newTeamDescription}
                      onChange={(e) => {
                        setNewTeamDescription(e.target.value);
                        if (validationErrors.description)
                          setValidationErrors((prev) => ({ ...prev, description: '' }));
                      }}
                      maxLength={50}
                      rows={3}
                    />
                    {validationErrors.description && (
                      <p className="text-xs text-red-600 dark:text-red-400 mt-1">
                        {validationErrors.description}
                      </p>
                    )}
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-foreground mb-1">
                      Daily Job Capacity
                    </label>
                    <Input
                      type="number"
                      min="1"
                      max="50"
                      placeholder="e.g. 10"
                      value={newTeamCapacity}
                      onChange={(e) => {
                        setNewTeamCapacity(e.target.value);
                        if (validationErrors.capacity)
                          setValidationErrors((prev) => ({ ...prev, capacity: '' }));
                      }}
                    />
                    <p className="text-xs text-muted-foreground mt-1">
                      Maximum jobs this team can carry out per day (1–50)
                    </p>
                    {validationErrors.capacity && (
                      <p className="text-xs text-red-600 dark:text-red-400 mt-1">
                        {validationErrors.capacity}
                      </p>
                    )}
                  </div>
                </div>

                <div className="flex items-center justify-between p-4 border-t border-border">
                  <div className="text-sm text-muted-foreground">Next: Add team members</div>
                  <div className="flex gap-2">
                    <Button variant="outline" onClick={handleCloseCreateModal}>
                      Cancel
                    </Button>
                    <Button
                      className="bg-orange-600 hover:bg-orange-700 text-white"
                      onClick={handleNextStage}
                      disabled={!newTeamName.trim()}
                    >
                      Next
                      <ArrowRight className="w-4 h-4 ml-2" />
                    </Button>
                  </div>
                </div>
              </>
            )}

            {/* Stage 2: Add Members */}
            {createStage === 2 && (
              <>
                <div className="flex-1 overflow-hidden flex flex-col">
                  <div className="p-4 border-b border-border">
                    <div className="bg-muted/50 rounded-lg p-3 mb-3">
                      <div className="text-sm font-medium text-foreground">{newTeamName}</div>
                      {newTeamDescription && (
                        <div className="text-xs text-muted-foreground mt-1">
                          {newTeamDescription}
                        </div>
                      )}
                    </div>
                    <div className="relative">
                      <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                      <Input
                        placeholder="Search users to add..."
                        value={memberSearchTerm}
                        onChange={(e) => setMemberSearchTerm(e.target.value)}
                        className="pl-9"
                      />
                    </div>
                    {selectedMembers.length > 0 && (
                      <div className="mt-2 text-sm text-muted-foreground">
                        {selectedMembers.length} member{selectedMembers.length !== 1 ? 's' : ''}{' '}
                        selected
                      </div>
                    )}
                  </div>

                  <div className="flex-1 overflow-y-auto p-4">
                    <div className="space-y-2">
                      {filteredAvailableUsers.length === 0 && (
                        <div className="text-center py-8 text-muted-foreground">
                          <p>No users available</p>
                        </div>
                      )}
                      {filteredAvailableUsers.map((user) => (
                        <div
                          key={user.id}
                          className={`flex items-center justify-between p-3 rounded-lg border transition-all cursor-pointer ${
                            selectedMembers.includes(user.id)
                              ? 'border-orange-600 bg-orange-50 dark:bg-orange-950/20'
                              : 'border-border hover:bg-muted/50'
                          }`}
                          onClick={() => toggleMember(user.id)}
                        >
                          <div className="flex items-center gap-3">
                            <Avatar
                              className={`w-9 h-9 ${selectedMembers.includes(user.id) ? 'bg-orange-600' : 'bg-blue-600'} text-white`}
                            >
                              <AvatarFallback
                                className={`${selectedMembers.includes(user.id) ? 'bg-orange-600' : 'bg-blue-600'} text-white text-sm`}
                              >
                                {getInitials(user.name)}
                              </AvatarFallback>
                            </Avatar>
                            <div>
                              <div className="font-medium text-foreground text-sm">{user.name}</div>
                              <div className="text-xs text-muted-foreground">{user.role}</div>
                            </div>
                          </div>
                          <div className="flex items-center gap-2">
                            {selectedMembers.includes(user.id) && (
                              <Badge className="bg-orange-600 text-white border-0">Selected</Badge>
                            )}
                            <Badge variant="outline" className="text-xs">
                              {user.team}
                            </Badge>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>

                {selectedMembers.length > 0 && (
                  <div className="px-4 pb-4 border-t border-border pt-4">
                    <label className="block text-sm font-medium text-foreground mb-1">
                      Team Lead
                    </label>
                    <Select
                      value={selectedLeadId}
                      onValueChange={(value) =>
                        setSelectedLeadId(value === '__none__' ? '' : value)
                      }
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="No team lead" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="__none__">No team lead</SelectItem>
                        {allUsers
                          .filter((u) => selectedMembers.includes(u.id))
                          .map((u) => (
                            <SelectItem key={u.id} value={u.id}>
                              {u.name}
                            </SelectItem>
                          ))}
                      </SelectContent>
                    </Select>
                    <p className="text-xs text-muted-foreground mt-1">
                      Choose a lead from the selected members (optional)
                    </p>
                  </div>
                )}

                <div className="flex items-center justify-between p-4 border-t border-border">
                  <Button variant="outline" onClick={handlePreviousStage}>
                    <ArrowLeft className="w-4 h-4 mr-2" />
                    Back
                  </Button>
                  <div className="flex gap-2">
                    <Button variant="outline" onClick={handleCloseCreateModal}>
                      Cancel
                    </Button>
                    <Button
                      className="bg-orange-600 hover:bg-orange-700 text-white"
                      onClick={handleCreateTeam}
                      disabled={isSubmitting}
                    >
                      {isSubmitting ? (
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      ) : (
                        <UserPlus className="w-4 h-4 mr-2" />
                      )}
                      Create Team
                    </Button>
                  </div>
                </div>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
